import { type ReactNode, useState } from "react";

import ToPayNow from "common/components/subscriptions/ToPayNow";
import Skeleton from "common/components/ui/Skeleton";
import useCustomerEstimateCreation from "common/datahooks/useCustomerEstimateCreation";
import useCustomerSubscriptionMutations from "common/datahooks/useCustomerSubscriptionMutations";
import {
  formatPrice,
  formatPricingPeriodInfo,
  trackMetaPixel,
} from "common/helpers/utils";
import type { Pricing } from "common/types";

import QuantitySelector from "./QuantitySelector";

const INCLUDED_SEATS = 20;
const MAX_SEATS = 999;

export default function AgencySubscription({
  children,
  currentStep,
  onSuccess,
  pricing,
}: {
  children: (params: {
    isNextStepDisabled: boolean;
    isNextButtonLoading: boolean;
    onCreateSubscription: (paymentSourceId: string) => void;
    mainContent: ReactNode;
  }) => ReactNode;
  currentStep: "manage" | "summary" | "payment" | "cancelAccounts" | "success";
  onSuccess: () => void;
  pricing: { addon: Pricing; plan: Pricing };
}) {
  const [seatCount, setSeatCount] = useState<number>(INCLUDED_SEATS);

  const { estimateCreation, isLoadingEstimateCreation } =
    useCustomerEstimateCreation({
      planPricingId: pricing.plan.id,
      quantity: seatCount,
    });
  const { createSubscription, isCreatingSubscription } =
    useCustomerSubscriptionMutations();

  const additionalSeatsCount = seatCount - INCLUDED_SEATS;
  const planPrice = estimateCreation
    ? additionalSeatsCount * (estimateCreation.pricePerItem / 100)
    : 0;

  async function onCreateSubscription(paymentSourceId: string) {
    const {
      subscription: { id },
    } = await createSubscription({
      planPricingId: pricing.plan.id,
      count: seatCount,
      paymentSourceId,
    });

    const eventData = {
      value: planPrice,
      currency: "USD",
      content_type: "subscription",
      content_ids: [id],
      num_items: seatCount,
    };

    trackMetaPixel("track", "Subscribe", eventData);
    trackMetaPixel("track", "Purchase", eventData);

    onSuccess();
  }

  const pricingDetailsContent = (
    <>
      <div className="flex items-center justify-between px-2 py-1 bg-black-100 rounded-xl">
        <div className="flex flex-col text-black-600 text-button-sm">
          <span>Base price</span>
          <span>(20 seats included)</span>
        </div>
        {isLoadingEstimateCreation ? (
          <Skeleton className="h-5 w-28" />
        ) : (
          <span className="text-md-medium text-black-500">
            {formatPrice(
              (estimateCreation.pricePerItem / 100) * 20 +
                estimateCreation.basePrice / 100,
            )}
            /{formatPricingPeriodInfo(pricing.plan)}
          </span>
        )}
      </div>
      {!!additionalSeatsCount && (
        <div className="flex items-center justify-between px-2">
          <div className="flex flex-col gap-y-0.5">
            <span className="text-black-600 text-button-sm">
              Additional seats
            </span>
            <span className="text-purple-600 text-xs-bold">
              Billed 20$ per seat
            </span>
          </div>
          {isLoadingEstimateCreation ? (
            <Skeleton className="h-5 w-28" />
          ) : (
            <span className="text-md-medium text-black-500">
              {formatPrice(
                additionalSeatsCount * (estimateCreation.pricePerItem / 100),
              )}
              /{formatPricingPeriodInfo(pricing.plan)}
            </span>
          )}
        </div>
      )}
      <div className="flex items-center justify-between px-2">
        <span className="text-button-sm text-black-600">No. of seats</span>
        <span className="text-md-medium text-black-500">{seatCount}</span>
      </div>
      <div className="flex items-center justify-between px-2">
        <span className="text-button-sm text-black-600">Price</span>
        {isLoadingEstimateCreation ? (
          <Skeleton className="h-5 w-28" />
        ) : (
          <span className="text-md-medium text-black-500">
            {formatPrice(estimateCreation.amountDue / 100)}/
            {formatPricingPeriodInfo(pricing.plan)}
          </span>
        )}
      </div>
      <ToPayNow
        estimate={estimateCreation}
        isLoading={isLoadingEstimateCreation}
        isDowngradeOrEqual={false}
        isInTrial={false}
      />
    </>
  );

  const mainContent =
    currentStep === "manage" ? (
      <>
        <QuantitySelector
          quantity={seatCount}
          setQuantity={setSeatCount}
          minValue={INCLUDED_SEATS}
          maxValue={MAX_SEATS}
        />
        <div className="flex flex-col gap-y-2.5">{pricingDetailsContent}</div>
      </>
    ) : (
      pricingDetailsContent
    );

  return children({
    isNextStepDisabled: !estimateCreation,
    isNextButtonLoading: isCreatingSubscription || isLoadingEstimateCreation,
    onCreateSubscription,
    mainContent,
  });
}
